import { Box, styled, Typography } from '@mui/material';
import { DeleteConfirmModal } from 'components/diligence/modals';
import { FilesList } from 'components/diligence/FilesList';
import { CircularProgressWithLabel } from 'components/shared/CircularProgressWithLabel';
import { FileOutput } from 'dtos/diligence/FileOutput';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadStore } from 'stores/UploadStore';
import { maxUploadSizeMb } from 'utils/config';
import { DiligenceStore } from 'stores/diligence/DiligenceStore';

const LinkSpan = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  textDecoration: 'underline',
  color: theme.palette.primary.main,
}));

type UploadAreaProps = {
  sx: any;
  files: FileOutput[];
  uploadStore?: UploadStore;
  diligenceStore: DiligenceStore;
  onDrop: (acceptedFiles: File[], rejections: any[]) => void;
  onDelete: (file: FileOutput) => Promise<void>;
  categories: string[];
};

const UploadArea = observer(
  ({ sx, files, uploadStore, diligenceStore, onDrop, onDelete, categories }: UploadAreaProps) => {
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const [confirmDeleteFile, setConfirmDeleteFile] = useState<FileOutput>();
    const [deleting, setDeleting] = useState(false);

    const handleDeleteClick = (event: React.MouseEvent, file: FileOutput) => {
      setTimeout(() => {
        setDeleting(false);
        setConfirmDeleteFile(file);
        setConfirmDeleteVisible(true);
      }, 1);
      event.stopPropagation();
      event.preventDefault();
    };

    const handleDelete = async () => {
      if (!confirmDeleteFile) return;
      setDeleting(true);
      await onDelete(confirmDeleteFile);
      setConfirmDeleteVisible(false);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple: true,
      disabled: uploadStore !== undefined,
      maxSize: maxUploadSizeMb * 1024 * 1024,
    });

    return (
      <>
        <DeleteConfirmModal
          visible={confirmDeleteVisible}
          disabled={deleting}
          file={confirmDeleteFile}
          onClose={() => setConfirmDeleteVisible(false)}
          onConfirm={handleDelete}
        />
        <Box
          sx={{
            minHeight: '88px',
            padding: { xs: '8px', sm: '16px' },
            border: '1px dashed rgba(66, 96, 211, 0.5)',
            borderRadius: '4px',
            backgroundColor: isDragActive ? 'rgba(200, 200, 200, 1)' : 'rgba(250, 250, 250, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            ...sx,
          }}
          data-testid="upload-area"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {uploadStore !== undefined ? (
            <div>
              <CircularProgressWithLabel value={uploadStore.percent} data-testid="upload-progress-meter" />
              <Typography variant="subtitle2" sx={{ marginTop: '4px' }} data-testid="upload-progress-stats">
                <strong>
                  Uploading {uploadStore.files.length} {uploadStore.files.length === 1 ? 'file' : 'files'}
                </strong>
              </Typography>
            </div>
          ) : (
            <div>
              <Typography variant="subtitle2">
                <strong>
                  Drag and drop documents or <LinkSpan>Browse Files</LinkSpan>
                </strong>
              </Typography>
              <Typography variant="body2" sx={{ marginTop: '2px' }}>
                Maximum file size {maxUploadSizeMb} MB
              </Typography>
            </div>
          )}
        </Box>
        {files.length > 0 && (
          <div style={{ marginTop: '24px', width: '100%' }}>
            <FilesList
              files={files}
              onDeleteClick={handleDeleteClick}
              filesStore={diligenceStore.filesStore}
              setCategorySubmitted={diligenceStore.setCategorySubmitted}
              setCategoryIncomplete={diligenceStore.setCategoryIncomplete}
              categories={categories}
            />
          </div>
        )}
      </>
    );
  }
);

export { UploadArea };
